import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthCard } from '../components/AuthCard';
import { MfaPairing } from '../components/MfaPairing';
import { resumeAuthorizeUrl } from '../lib/api';
import { postLoginDestination } from '../lib/products';
import { useAuthStore } from '../store/auth.store';

/**
 * Appairage OBLIGATOIRE, imposé aux comptes que le serveur signale par
 * `mfaRequired` — aujourd'hui les superadmins.
 *
 * La connexion s'est bien passée : la session EXISTE. Ce n'est donc pas une
 * étape d'authentification de plus, c'est une porte posée après, sur le chemin
 * de la destination demandée. D'où trois choix :
 *
 *  - aucun bouton pour renoncer. La seule sortie est la déconnexion, et elle
 *    est nommée comme telle plutôt que déguisée en « plus tard » ;
 *  - le flux OIDC en cours est CONSERVÉ dans l'URL et repris une fois
 *    l'appairage fait : quelqu'un qui venait ouvrir SEmplyApp y arrive, il a
 *    seulement appairé son téléphone en chemin ;
 *  - un compte qui n'a rien à appairer n'a rien à faire ici et repart aussitôt
 *    — l'URL peut être atteinte à la main, ou rechargée après coup.
 */
export function MfaEnrolmentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading, fetchMe, logout } = useAuthStore();

  const returnTo = resumeAuthorizeUrl(searchParams);

  useEffect(() => {
    void fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate('/login');
    else if (!user.mfaRequired) void leave();
    // `leave` est stable pour la durée de vie de la page (elle ne dépend que
    // de `returnTo`, figé à l'arrivée) : l'exclure évite une boucle d'effets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, navigate]);

  async function leave() {
    if (returnTo) {
      window.location.assign(returnTo);
      return;
    }
    const destination = await postLoginDestination(searchParams);
    if ('external' in destination) window.location.assign(destination.external);
    else navigate(destination.internal);
  }

  if (loading || !user) {
    return <AuthCard title="Chargement…">{null}</AuthCard>;
  }

  return (
    <AuthCard
      title="Double authentification requise"
      subtitle="Votre compte administre la suite SEmply : un second facteur est obligatoire."
    >
      <MfaPairing onDone={() => void leave()} />
      <button
        className="btn btn-secondary"
        type="button"
        style={{ marginTop: '1rem' }}
        onClick={() => void logout().then(() => navigate('/login'))}
      >
        Se déconnecter
      </button>
    </AuthCard>
  );
}
